/**
 * StatusDot - 8px filled circle status indicator with optional pulse ring.
 *
 * A pure SVG status indicator for inline use. Supports all semantic status
 * kinds and an optional pulsing animation for live/active states.
 *
 * @example Basic usage
 * ```tsx
 * <StatusDot kind="success" />
 * <StatusDot kind="warning" pulse />
 * <StatusDot kind="error" size={12} />
 * ```
 *
 * @example With accessibility label
 * ```tsx
 * <StatusDot kind="success" label="Connection active" />
 * ```
 */

import type { CSSProperties } from 'react'
import * as tokens from '@/lib/tokens'

// ── Types ────────────────────────────────────────────────────────────────────

/**
 * Semantic status kinds mapping to design system tokens.
 */
export type StatusKind = 'success' | 'warning' | 'error' | 'info' | 'neutral' | 'pending'

export interface StatusDotProps {
  /** Semantic status kind */
  kind: StatusKind
  /** Dot size in pixels (default: 8) */
  size?: number
  /** Enable pulse animation (default: false) */
  pulse?: boolean
  /** Accessibility label for screen readers */
  label?: string
}

// ── Color Mapping ────────────────────────────────────────────────────────────

const kindToColor: Record<StatusKind, string> = {
  success: tokens.success,
  warning: tokens.warning,
  error: tokens.error,
  info: tokens.info,
  neutral: tokens.textMuted,
  pending: tokens.textFaint,
}

// ── Component ────────────────────────────────────────────────────────────────

/**
 * StatusDot renders a small colored circle indicating status.
 *
 * Uses native SVG for crisp rendering at any size. When `pulse` is true,
 * renders an expanding ring animation behind the dot.
 */
export function StatusDot({
  kind,
  size = 8,
  pulse = false,
  label,
}: StatusDotProps) {
  const color = kindToColor[kind]

  // Ensure positive size (guard against zero, negative, NaN, Infinity)
  const safeSize = Number.isFinite(size) && size > 0 ? size : 8

  // Pulse ring needs extra space
  const viewBoxSize = pulse ? safeSize * 3 : safeSize
  const center = viewBoxSize / 2

  return (
    <svg
      width={viewBoxSize}
      height={viewBoxSize}
      viewBox={`0 0 ${viewBoxSize} ${viewBoxSize}`}
      style={styles.svg}
      role="status"
      aria-label={label}
    >
      {pulse && (
        <circle
          cx={center}
          cy={center}
          r={safeSize / 2}
          fill={color}
          className="status-dot-ring"
          style={{ transformOrigin: 'center' }}
        />
      )}
      <circle
        cx={center}
        cy={center}
        r={safeSize / 2}
        fill={color}
        className={pulse ? 'status-dot-pulse' : undefined}
        style={{ transformOrigin: 'center' }}
      />
    </svg>
  )
}

// ── Styles ───────────────────────────────────────────────────────────────────

const styles: Record<string, CSSProperties> = {
  svg: {
    flexShrink: 0,
    verticalAlign: 'middle',
  },
}

export default StatusDot
